import { Button, Checkbox,Input, message, Modal } from "antd";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import React, { useState } from "react";
import { supabase } from "../../../../../config/supabaseClient";
import { storeUserLinks, storeUserLinksBulk } from "../../../../../Utils/SupabaseServices/UserLinksDatabase";

const ImportLinksModal = (props: any) => {
  const {
    isMigrateModalOpen,
    setIsMigrateModalOpen,
    user_id,
    username,
    name,
    from,
    componentId,
    refetch,
    getContainer = false,
    zIndex,
  } = props;
  const [migrateUrl, setMigrateUrl] = useState("");
  const [isScraping, setIsScraping] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  interface ScrapedLink {
    name: string;
    url: string;
    image: string | null;
    selected: boolean;
  }
  const [scrapedLinks, setScrapedLinks] = useState<ScrapedLink[]>([]);
  const scrapeLinktree = async () => {
    if (!migrateUrl.trim()) {
      message.error("Please enter a valid URL");
      return;
    }

    try {
      new URL(migrateUrl);
    } catch (_) {
      message.error("Please enter a valid URL format (e.g., https://beacons.ai/...).");
      return;
    }

    setIsScraping(true);
    try {
      const { data, error } = await supabase.functions.invoke("scrape-linktree", {
        body: { url: migrateUrl },
      });

      if (error) {
        // Use the detailed error from the function, or a fallback
        const errorMessage = error.message || 'Failed to scrape links. The page might be protected or the service may be temporarily down.';
        throw new Error(errorMessage);
      }

      if (data.links && data.links.length > 0) {
        // Start unchecked so creators choose which links to import
        // (Select All / individual boxes should look empty until picked).
        setScrapedLinks(
          data.links.map((link: any) => ({ ...link, selected: false })),
        );
        message.success(`Found ${data.links.length} links!`);
      } else {
        message.warning(data.message || "No links found. The URL might be protected or using a different structure.");
      }
    } catch (error: any) {
      console.error('Scraping error:', error);
      message.error(error.message || "An unknown error occurred during scraping.");
    } finally {
      setIsScraping(false);
    }
  };

  const resetMigrateState = () => {
    setMigrateUrl("");
    setScrapedLinks([]);
    setIsScraping(false);
    setIsImporting(false);
  };

  const handleMigrateModalCancel = () => {
    setIsMigrateModalOpen(false);
    resetMigrateState();
  };

  const importScrapedLinks = async () => {
    const selectedLinks = scrapedLinks.filter((link) => link.selected);
    if (selectedLinks.length === 0) {
      message.warning("Select at least one link to import.");
      return;
    }

    setIsImporting(true);
    try {
      // Import each scraped link
      for (let i = 0; i < selectedLinks.length; i++) {
        const link = selectedLinks[i];
        
        // Save to database using storeUserLinks
        await storeUserLinksBulk(
          null, // null for new record
          user_id,
          username,
          link.name,
          link.url,
          link.image,
          name, // component name
          from,
          componentId
        );
      }

      message.success(`Successfully imported ${selectedLinks.length} links!`);
      setIsMigrateModalOpen(false);
      resetMigrateState();
      
      // Refresh the data
      refetch();
    } catch (error) {
      console.error('Import error:', error);
      message.error("Failed to import links. Please try again.");
    } finally {
      setIsImporting(false);
    }
  };

  const handleToggleLinkSelection = (index: number) => {
    setScrapedLinks((prev) =>
      prev.map((link, i) =>
        i === index ? { ...link, selected: !link.selected } : link,
      ),
    );
  };

  const handleSelectAllToggle = (e: CheckboxChangeEvent) => {
    const checked = e.target.checked;
    setScrapedLinks((prev) =>
      prev.map((link) => ({ ...link, selected: checked })),
    );
  };
  const selectedLinksCount = scrapedLinks.filter(link => link.selected).length;

  return (
    <>
      <Modal
        title="Import Links from Page"
        open={isMigrateModalOpen}
        onCancel={handleMigrateModalCancel}
        footer={[
          <Button key="cancel" onClick={handleMigrateModalCancel}>
            Cancel
          </Button>,
          // Show only Scrape button if no links are scraped yet
          ...(scrapedLinks.length === 0
            ? [
                <Button
                  key="scrape"
                  type="primary"
                  loading={isScraping}
                  onClick={scrapeLinktree}
                  disabled={!migrateUrl.trim()}
                >
                  {isScraping ? "Scraping..." : "Scrape"}
                </Button>,
              ]
            : [
                <Button
                  key="import"
                  type="primary"
                  onClick={importScrapedLinks}
                  disabled={selectedLinksCount === 0}
                  loading={isImporting}
                >
                {isImporting ? "Importing..." : `Import ${selectedLinksCount > 0 ? `(${selectedLinksCount})` : ""}`}
                </Button>,
              ]),
        ]}
        width={600}
        focusTriggerAfterClose={false}
        getContainer={getContainer}
        zIndex={zIndex}
      >
        <div style={{ marginBottom: "16px" }}>
          <p>
            Paste a page URL (e.g., from Linktree, Beacons) to import its links:
          </p>
          <Input
            placeholder="https://your-page.com/links"
            value={migrateUrl}
            onChange={(e) => setMigrateUrl(e.target.value)}
            onPressEnter={scrapeLinktree}
            disabled={isScraping}
          />
        </div>

        {scrapedLinks.length > 0 && (
          <div>
            <div style={{ padding: "8px 0" }}>
              <Checkbox
                checked={selectedLinksCount === scrapedLinks.length}
                indeterminate={
                  selectedLinksCount > 0 &&
                  selectedLinksCount < scrapedLinks.length
                }
                onChange={handleSelectAllToggle}
              >
                Select All ({selectedLinksCount} / {scrapedLinks.length})
              </Checkbox>
            </div>
            <div
              style={{
                maxHeight: "300px",
                overflowY: "auto",
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
                padding: "8px",
              }}
            >
              {scrapedLinks.map((link, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px",
                    borderBottom:
                      index < scrapedLinks.length - 1
                        ? "1px solid #f0f0f0"
                        : "none",
                    gap: "12px",
                  }}
                >
                  <Checkbox
                    checked={link.selected}
                    onChange={() => handleToggleLinkSelection(index)}
                  />
                  {link.image && (
                    <img
                      src={link.image}
                      alt=""
                      style={{
                        width: "32px",
                        height: "32px",
                        objectFit: "cover",
                        borderRadius: "4px",
                      }}
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: "bold",
                        fontSize: "14px",
                        marginBottom: "2px",
                      }}
                    >
                      {link.name}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        wordBreak: "break-all",
                      }}
                    >
                      {link.url}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default ImportLinksModal;
